import React, { useCallback } from 'react';
import { produce } from 'immer';
import localForage from 'localforage';

import useRequestJSON from '../../libs/hooks/useRequestJSON';
import { PostJSON, PaginatedParams } from '../../libs/request';
import { IUser } from '../../libs/user';
import { IPatientGroup } from '../../libs/group';
import { useAuth } from '../../contexts/auth';

import { createPatientGroupData } from './utils';

import {
  PatientGroupResponse,
  PatientGroupData,
  PatientGroupValue
} from './types';
import { useUrlState } from '../../contexts/active-dialogs/url';

const localStore = localForage.createInstance({
  name: 'csPatientGroup'
});

const KEY_GROUP_ID = 'cs_activepg_id';
const KEY_USER_ID = 'cs_user_id';
const ALL_PATIENTS = -1;

const PatientGroupContext = React.createContext<PatientGroupValue>({
  data: null,
  isEnterprise: null,
  isLoading: true,
  error: null,
  getActiveGroup: null,
  setActiveGroup: null,
  updateGroup: null,
  removeGroup: null
});

const transform = async (
  user: IUser,
  response: PatientGroupResponse
): Promise<PatientGroupData> => {
  // Construct the group data
  const pgData = createPatientGroupData(response);
  const userId: number = await localStore.getItem(KEY_USER_ID);
  const groupId: number = await localStore.getItem(KEY_GROUP_ID);
  // Only use the ID from the local storage if user ID matches
  if (user?.id === userId && groupId != null) {
    pgData.activeGroupId = groupId;
  }
  /**
   * Users associated with accounts where isEnterprise = true
   * shouldn't be able to select the "All Patients View".
   *
   * For users who might have the all patients view saved in
   * their local storage (represented by a -1 group id),
   * we want to override that here.
   */
  if (user?.account?.isEnterprise && pgData.activeGroupId === -1) {
    pgData.activeGroupId = pgData.groups[0].id;
    localStore.setItem(KEY_GROUP_ID, pgData.activeGroupId);
  }
  return pgData;
};

const createParams = (): PaginatedParams => {
  return {
    limit: 100
  };
};

export const PatientGroupProvider: React.FC = (props) => {
  // Fetch the user (required to validate the saved group ID)
  const { user } = useAuth();
  const { isViewingPatientFromUrl } = useUrlState();
  // Fetch the patient groups
  const request = useCallback(
    () =>
      PostJSON<PatientGroupResponse, PaginatedParams>({
        url: '/patient/data_groups/',
        params: createParams()
      }),
    []
  );
  const { data, setData, isLoading, error } = useRequestJSON<
    PatientGroupResponse,
    PatientGroupData
  >({
    request,
    transformFn: transform.bind(null, user),
    isTransformAsync: true,
    enabled: !isViewingPatientFromUrl
  });

  const getGroup = (id: number): IPatientGroup => {
    if (!data) {
      return;
    }
    if (!id || id === ALL_PATIENTS) {
      return null;
    }
    return data.groups.find((patientGroup) => patientGroup.id === id);
  };

  /**
   * Since we store the selected patient group
   * only by id, this method allows us to get the full selected
   * patient group back
   */
  const getActiveGroup = () => {
    const id = data ? data.activeGroupId : null;
    return getGroup(id);
  };

  /**
   * Set active group by id
   *
   * @param groupId group id to set to active,
   * if groupId not passed in, then set to null to indicate
   * that the user wants to view all patients
   */
  const setActiveGroup = (
    groupId?: number,
    updatedGroups?: IPatientGroup[]
  ) => {
    Promise.all([
      localStore.setItem(KEY_GROUP_ID, groupId || -1),
      localStore.setItem(KEY_USER_ID, user.id)
    ]).catch((error) => console.warn(error));
    setData({
      activeGroupId: groupId ? groupId : null,
      groups: updatedGroups ? updatedGroups : data.groups
    });
  };

  /**
   * Updates a specific group in the patient group provider state
   * OR if that group doesn't exist, adds it
   * Note: setData overrides the provider data state completely,
   * which is why we must include activeGroupId here
   *
   * @param id id of group you want to update
   * @param group the updated group object
   */
  const updateGroup = (group: IPatientGroup) => {
    const activeGroupId = group.id;
    const updatedGroups = produce(data.groups, (draft) => {
      const index = data.groups.findIndex((grp) => grp.id === group.id);
      if (index === -1) {
        draft.push(group);
      } else {
        draft[index] = group;
      }
      // Can only have one default group at a time
      if (group.isDefault) {
        draft.forEach((grp) => {
          if (grp.id !== group.id) {
            grp.isDefault = false;
          }
        });
      }
    });
    setActiveGroup(activeGroupId, updatedGroups);
  };

  const removeGroup = (id: number) => {
    const updatedGroups = data.groups.filter(
      (group: IPatientGroup) => group.id !== id
    );
    const activeGroup =
      updatedGroups.length > 0 ? updatedGroups[updatedGroups.length - 1] : null;
    const activeGroupId = activeGroup ? activeGroup.id : null;
    setActiveGroup(activeGroupId, updatedGroups);
  };

  const value: PatientGroupValue = {
    data,
    isEnterprise: user?.account?.isEnterprise,
    isLoading: isViewingPatientFromUrl ? false : isLoading,
    error,
    getActiveGroup,
    setActiveGroup,
    updateGroup,
    removeGroup
  };

  return <PatientGroupContext.Provider value={value} {...props} />;
};

export function usePatientGroup(): PatientGroupValue {
  const context = React.useContext(PatientGroupContext);
  if (context === undefined) {
    throw new Error(
      'usePatientGroup must be used within a PatientGroupProvider'
    );
  }
  return context;
}
